import React, { useState } from 'react'

import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline'
import PeopleIcon from '@material-ui/icons/People'
import PersonIcon from '@material-ui/icons/Person'
import {
    Container,
    LocationForm,
    ServiceDots,
    Selector,
} from '../../components'
import {
    AreaConfig,
    AreaStatusConfig,
    AreaStatusTag,
    BaseWorkorder,
    Folder,
    ModelListMap,
    ModelMap,
    UnitOccupanyType,
    getUnitOccupancyCountMap,
    getUnitOccupancyType,
} from '../../models'
import { FinderSelection } from '../../hooks'
import {
    Checkbox,
    Popover,
    Tooltip,
    useTheme,
    Button,
    Divider,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@material-ui/core'
import { usePopover } from '../../hooks/usePopover'
import {
    AreaStatusTagListThunk,
    BulkSwapAreaStatusTagRequest,
} from '../../store'
import { toast } from 'react-toastify'
import { typographyStyle } from '../../theme/typographyStyle'

export interface ScheduleFinderProps {
    root: Folder
    finderSelection: FinderSelection
    areaConfigMap: ModelMap<AreaConfig>
    areaStatusTagMap: ModelMap<AreaStatusTag>
    unitWorkorderMap: ModelListMap<BaseWorkorder>
    style?: React.CSSProperties
    defaultOpen?: boolean
    useCase?: 'inspection' | 'schedule'
    areaStatusConfigList?: AreaStatusConfig[]
    bulkSwapAreaStatusTag?: (
        req: BulkSwapAreaStatusTagRequest,
    ) => AreaStatusTagListThunk
    scheduleId?: number
}

export const SchedulerFinder = (props: ScheduleFinderProps) => {
    const {
        root,
        finderSelection,
        areaConfigMap,
        areaStatusTagMap,
        unitWorkorderMap,
        useCase,
        areaStatusConfigList,
        bulkSwapAreaStatusTag,
    } = props

    const { isOpen, anchorEl, handleOpen, handleClose } = usePopover()

    const [openWorkorders, setOpenWorkorders] = useState<
        BaseWorkorder[] | null
    >(null)

    // State for bulk swap functionality
    const [oldAreaStatusConfigId, setOldAreaStatusConfigId] = useState<number>(
        -1,
    )
    const [newAreaStatusConfigId, setNewAreaStatusConfigId] = useState<number>(
        -1,
    )
    const [isBulkSwapOpen, setIsBulkSwapOpen] = useState<boolean>(false)
    const [isBulkSwapLoading, setIsBulkSwapLoading] = useState<boolean>(false)
    const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false)

    const theme = useTheme()

    // Get selected units
    const selectedUnits = finderSelection.getUnits()

    // Handle bulk swap
    const handleBulkSwap = async () => {
        if (
            !bulkSwapAreaStatusTag ||
            !areaStatusConfigList ||
            selectedUnits.length === 0
        ) {
            return
        }

        if (oldAreaStatusConfigId === -1 || newAreaStatusConfigId === -1) {
            return
        }

        if (!props.scheduleId) {
            return
        }

        setShowConfirmDialog(true)
    }

    // Execute bulk swap after confirmation
    const executeBulkSwap = async () => {
        if (
            !bulkSwapAreaStatusTag ||
            !areaStatusConfigList ||
            selectedUnits.length === 0
        ) {
            return
        }

        if (oldAreaStatusConfigId === -1 || newAreaStatusConfigId === -1) {
            return
        }

        if (!props.scheduleId) {
            return
        }

        setIsBulkSwapLoading(true)
        setShowConfirmDialog(false)

        try {
            const request: BulkSwapAreaStatusTagRequest = {
                body: {
                    unit_ids: selectedUnits.map((unit) => unit.id),
                    schedule: props.scheduleId,
                    old_area_status_config: oldAreaStatusConfigId,
                    new_area_status_config: newAreaStatusConfigId,
                    locked: true,
                },
            }

            await bulkSwapAreaStatusTag(request)
            setIsBulkSwapOpen(false)
            setOldAreaStatusConfigId(-1)
            setNewAreaStatusConfigId(-1)
            toast.success(
                `Successfully swapped area statuses for ${
                    selectedUnits.length
                } unit${selectedUnits.length !== 1 ? 's' : ''}`,
            )
        } catch (error) {
            // Error handling - you might want to show a toast or error message here
            console.error('Bulk swap failed:', error)
            toast.error('Failed to swap area statuses. Please try again.')
        } finally {
            setIsBulkSwapLoading(false)
        }
    }

    return (
        <Container direction="column" style={props.style}>
            {/* Bulk Swap Area Status Tag Section */}
            {areaStatusConfigList && bulkSwapAreaStatusTag && (
                <Container
                    direction="column"
                    style={{
                        padding: theme.spacing(1.5),
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        backgroundColor: isBulkSwapOpen
                            ? theme.palette.grey[50]
                            : theme.palette.grey[100],
                        transition: 'background-color 0.2s ease',
                    }}
                >
                    <Container
                        style={{
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: isBulkSwapOpen ? theme.spacing(1) : 0,
                        }}
                    >
                        <Container
                            style={{
                                alignItems: 'center',
                                gap: theme.spacing(0.5),
                            }}
                        >
                            <span
                                style={{
                                    ...typographyStyle(
                                        theme.typography.subtitle2,
                                    ),
                                    fontWeight:
                                        theme.typography.fontWeightMedium,
                                    color: theme.palette.text.primary,
                                }}
                            >
                                Bulk Swap Area Status
                            </span>
                            {!isBulkSwapOpen && selectedUnits.length > 0 && (
                                <span
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.caption,
                                        ),
                                        backgroundColor:
                                            theme.palette.primary.main,
                                        color:
                                            theme.palette.primary.contrastText,
                                        padding: theme.spacing(0.25, 0.5),
                                        borderRadius: theme.shape.borderRadius,
                                        fontSize: '0.7rem',
                                    }}
                                >
                                    {selectedUnits.length}
                                </span>
                            )}
                        </Container>
                        <Button
                            variant="text"
                            size="small"
                            onClick={() => setIsBulkSwapOpen(!isBulkSwapOpen)}
                            style={{
                                minWidth: 'auto',
                                padding: theme.spacing(0.5, 1),
                                textTransform: 'none',
                                fontSize: typographyStyle(
                                    theme.typography.caption,
                                ).fontSize,
                            }}
                        >
                            {isBulkSwapOpen ? 'Hide' : 'Show'}
                        </Button>
                    </Container>

                    {isBulkSwapOpen && (
                        <Container
                            direction="column"
                            style={{ gap: theme.spacing(1.5) }}
                        >
                            <Container
                                direction="column"
                                style={{ gap: theme.spacing(1) }}
                            >
                                <Selector
                                    label="Current Area Status"
                                    currentValue={oldAreaStatusConfigId}
                                    onChange={(event) => {
                                        setOldAreaStatusConfigId(
                                            event.target.value as number,
                                        )
                                    }}
                                    data={areaStatusConfigList}
                                    getDisplayString={(config) => config.name}
                                    customStyle={{
                                        formControl: {
                                            width: '100%',
                                            margin: 0,
                                        },
                                        select: {
                                            fontSize: typographyStyle(
                                                theme.typography.body2,
                                            ).fontSize,
                                        },
                                    }}
                                    size="small"
                                />

                                <Selector
                                    label="New Area Status"
                                    currentValue={newAreaStatusConfigId}
                                    onChange={(event) => {
                                        setNewAreaStatusConfigId(
                                            event.target.value as number,
                                        )
                                    }}
                                    data={areaStatusConfigList}
                                    getDisplayString={(config) => config.name}
                                    customStyle={{
                                        formControl: {
                                            width: '100%',
                                            margin: 0,
                                        },
                                        select: {
                                            fontSize: typographyStyle(
                                                theme.typography.body2,
                                            ).fontSize,
                                        },
                                    }}
                                    size="small"
                                />
                            </Container>

                            <Container
                                style={{
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: theme.spacing(0.5, 0),
                                }}
                            >
                                <span
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.caption,
                                        ),
                                        color: theme.palette.text.secondary,
                                    }}
                                >
                                    {selectedUnits.length} unit
                                    {selectedUnits.length !== 1 ? 's' : ''}{' '}
                                    selected
                                </span>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    disabled={
                                        oldAreaStatusConfigId === -1 ||
                                        newAreaStatusConfigId === -1 ||
                                        selectedUnits.length === 0 ||
                                        isBulkSwapLoading
                                    }
                                    onClick={handleBulkSwap}
                                    style={{
                                        textTransform: 'none',
                                        fontSize: typographyStyle(
                                            theme.typography.caption,
                                        ).fontSize,
                                        padding: theme.spacing(0.5, 1.5),
                                        minWidth: 'auto',
                                    }}
                                >
                                    {isBulkSwapLoading ? (
                                        <Container
                                            style={{
                                                alignItems: 'center',
                                                gap: theme.spacing(0.5),
                                            }}
                                        >
                                            <CircularProgress
                                                size={12}
                                                color="inherit"
                                                style={{
                                                    color:
                                                        theme.palette.primary
                                                            .contrastText,
                                                }}
                                            />
                                            <span>Saving...</span>
                                        </Container>
                                    ) : (
                                        'Apply'
                                    )}
                                </Button>
                            </Container>
                        </Container>
                    )}
                </Container>
            )}

            {/* Original LocationForm */}
            <LocationForm
                root={root}
                locationSelection={finderSelection.selection}
                setLocationSelection={finderSelection.setFinderSelection}
                selectedIcon={<></>}
                getUnitAdornment={(unit) => {
                    const uOccMap = getUnitOccupancyCountMap(
                        unit,
                        areaConfigMap,
                        areaStatusTagMap,
                    )
                    const uOccType = getUnitOccupancyType(uOccMap)

                    let icon: JSX.Element | null = null
                    switch (uOccType) {
                        case UnitOccupanyType.PARTIAL:
                            icon = (
                                <Tooltip title="Partial Service">
                                    <PersonIcon />
                                </Tooltip>
                            )
                            break
                        case UnitOccupanyType.FULL_VACANT:
                            icon = (
                                <Tooltip title="Full Service">
                                    <PeopleIcon />
                                </Tooltip>
                            )
                            break
                        default:
                            icon = (
                                <Tooltip title="No Service">
                                    <PeopleOutlineIcon />
                                </Tooltip>
                            )
                    }

                    return (
                        <Container style={{ flex: 1 }}>
                            <Container
                                style={{
                                    width: 24,
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    marginRight: 2,
                                }}
                            >
                                {icon}
                            </Container>

                            <ServiceDots
                                workorders={unitWorkorderMap[unit.id] ?? []}
                                popOverAnchror={anchorEl}
                                openPopover={(e, workorders) => {
                                    setOpenWorkorders(workorders)
                                    handleOpen(e)
                                }}
                            />

                            <Popover
                                anchorEl={anchorEl}
                                open={isOpen}
                                onClose={() => {
                                    setOpenWorkorders(null)
                                    handleClose()
                                }}
                            >
                                <Container
                                    direction="column"
                                    style={{ padding: theme.spacing(1) }}
                                >
                                    {openWorkorders?.map((wo) => {
                                        return (
                                            <Container
                                                key={`OPEN_WO_${wo.id}`}
                                                direction="column"
                                                style={{
                                                    marginBottom: theme.spacing(
                                                        1,
                                                    ),
                                                    borderBottom: `1px solid black`,
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        ...typographyStyle(
                                                            theme.typography
                                                                .body1,
                                                        ),
                                                        fontWeight:
                                                            theme.typography
                                                                .fontWeightBold,
                                                    }}
                                                >
                                                    {wo.service_name ?? ''}
                                                </span>
                                                <span
                                                    style={{
                                                        ...typographyStyle(
                                                            theme.typography
                                                                .body2,
                                                        ),
                                                        fontWeight:
                                                            theme.typography
                                                                .fontWeightMedium,
                                                    }}
                                                >
                                                    {wo.vendor_name}
                                                </span>
                                            </Container>
                                        )
                                    })}
                                </Container>
                            </Popover>

                            <div style={{ flex: 1 }} />
                            <Checkbox
                                checked={
                                    finderSelection.selection.unit[unit.id] !==
                                    undefined
                                }
                                onClick={() => {
                                    finderSelection.setFinderSelection({
                                        type: 'unit',
                                        location: unit,
                                    })
                                }}
                            />
                        </Container>
                    )
                }}
                getFolderAdornment={(folder) => {
                    return (
                        <Container style={{ flex: 1 }}>
                            <div style={{ flex: 1 }} />
                            <Checkbox
                                color={'primary'}
                                checked={
                                    finderSelection.selection.folder[
                                        folder.id
                                    ] !== undefined
                                }
                                onClick={(e) => {
                                    e.stopPropagation()
                                    finderSelection.setFinderSelection({
                                        type: 'folder',
                                        location: folder,
                                    })
                                }}
                            />
                        </Container>
                    )
                }}
                useCase={useCase}
            />

            {/* Confirmation Dialog */}
            <Dialog
                open={showConfirmDialog}
                onClose={() => setShowConfirmDialog(false)}
                aria-labelledby="confirm-dialog-title"
                aria-describedby="confirm-dialog-description"
            >
                <DialogTitle id="confirm-dialog-title">
                    Confirm Area Status Swap
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="confirm-dialog-description">
                        Are you sure you want to swap the area status from{' '}
                        <strong>
                            {
                                areaStatusConfigList?.find(
                                    (config) =>
                                        config.id === oldAreaStatusConfigId,
                                )?.name
                            }
                        </strong>{' '}
                        to{' '}
                        <strong>
                            {
                                areaStatusConfigList?.find(
                                    (config) =>
                                        config.id === newAreaStatusConfigId,
                                )?.name
                            }
                        </strong>{' '}
                        for {selectedUnits.length} selected unit
                        {selectedUnits.length !== 1 ? 's' : ''}?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setShowConfirmDialog(false)}
                        color="primary"
                        disabled={isBulkSwapLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={executeBulkSwap}
                        color="primary"
                        variant="contained"
                        disabled={isBulkSwapLoading}
                        style={{ textTransform: 'none' }}
                    >
                        {isBulkSwapLoading ? (
                            <Container
                                style={{
                                    alignItems: 'center',
                                    gap: theme.spacing(0.5),
                                }}
                            >
                                <CircularProgress
                                    size={16}
                                    color="inherit"
                                    style={{
                                        color:
                                            theme.palette.primary.contrastText,
                                    }}
                                />
                                <span>Updating...</span>
                            </Container>
                        ) : (
                            'Confirm Swap'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    )
}
